import { goalsPending } from "../game/goalEvents.ts";
import { enterAge } from "../art/craftFeel.ts";
import { creatureIcon, creatureSprite } from "../art/creatures.ts";
import { itemIcon } from "../art/icons.ts";
import { interior } from "../art/interiors.ts";
import { mix, TYPE_COL, UI, withA, type Col } from "../art/pal.ts";
import { ITEMS, SHOP_STOCK, CURRENCY, itemHeal, stallStock, type ItemId, type OutfitId } from "../data/items.ts";
import { guideEvolution, guideHabitats } from "../data/fieldGuide.ts";
import { ZONES, zoneAt } from "../data/map.ts";
import { SPECIES, SPECIES_IDS, type SpeciesId } from "../data/species.ts";
import { TRAINERS } from "../data/trainers.ts";
import { moveById, moveEffectLabel, peekMove } from "../data/moves.ts";
import { fitText, measureText } from "../engine/font.ts";
import { easeOut, SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import { expToNext, healMon, LEVEL_CAP, maxHp, restoreAll, statOf, type Mon } from "../game/mon.ts";
import { bagAdd, bagCount, clinicHeal, PARTY_MAX, persist, stashMon } from "../game/save.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { backdrop, coin, cursor, dialog, focus, glass, hint, meter, titleBar, typeBadge } from "../ui/kit.ts";
import { wrapIndex } from "../ui/menu.ts";
import { actorFrame, wornOutfit } from "../world/actors.ts";
import { dayLight, worldHour } from "../world/fx.ts";

function isNight(ctx: Ctx): boolean {
  const q = new URLSearchParams(typeof window !== "undefined" ? window.location?.search ?? "" : "");
  return dayLight(worldHour(ctx.state, q.get("time"))).night > 0.5;
}

function playTime(ms: number): string {
  const m = Math.floor(ms / 60000);
  return `${Math.floor(m / 60)}h ${(m % 60).toString().padStart(2, "0")}m`;
}

/** Slide/fade-in helper for menu chrome. */
function enter(age: number, reduced: boolean, ms = 220): number {
  return reduced ? 1 : easeOut(Math.min(1, age / ms));
}

/** A crew row: icon, name, level, types, HP meter. */
function crewRow(g: Gfx, m: Mon, x: number, y: number, w: number, on: boolean, now: number, reduced: boolean, lead: boolean): void {
  glass(g, x, y, w, 27, { r: 6, tone: on ? UI.bg3 : UI.bg2, border: on ? UI.gold : UI.line });
  const sp = SPECIES[m.speciesId];
  const faint = m.hp <= 0;
  g.disc(x + 18, y + 14, 12, withA(TYPE_COL[sp?.types[0] ?? "cope"]!.fill, 60));
  const bob = on && !reduced ? Math.round(Math.abs(Math.sin(now / 220)) * 2) : 0;
  g.blit(creatureIcon(m.speciesId, !!m.shiny), x + 10, y + 5 - bob, { tint: faint ? [60, 60, 80, 255] : undefined, tintAmt: faint ? 0.7 : 0 });
  g.textShadow(x + 36, y + 4, fitText(`${on ? ">" : ""}${m.nick}`, 84), on ? UI.goldHi : UI.text, UI.ink);
  if (m.shiny) g.text(x + 12, y + 1, "✦", UI.goldHi);
  if (lead) {
    const lx = x + 38 + Math.min(80, measureText(m.nick));
    g.rrect(lx, y + 3, 26, 10, 4, UI.gold);
    g.text(lx + 3, y + 5, "LEAD", UI.paperInk);
  }
  const lv = `Lv${m.level}`;
  g.textShadow(x + w - measureText(lv) - 8, y + 4, lv, UI.gold, UI.ink);
  let bx = x + 36;
  for (const type of sp?.types ?? []) bx += typeBadge(g, bx, y + 14, type, true) + 2;
  const hp = `${m.hp}/${maxHp(m)}`;
  meter(g, x + w - 110, y + 17, 64, 5, m.hp / maxHp(m));
  g.text(x + w - measureText(hp) - 8, y + 15, hp, faint ? UI.red : UI.text);
  if (on) focus(g, x - 1, y - 1, w + 2, 29, now, reduced, 7);
}

function statBar(g: Gfx, x: number, y: number, label: string, v: number, max: number, c: Col, w = 90): void {
  g.text(x, y, label, UI.textDim);
  g.textR(x + 44, y, String(v), UI.text);
  meter(g, x + 48, y + 1, w, 5, v / max, { tone: { fill: c, hi: mix(c, UI.white, 0.45), lo: mix(c, UI.black, 0.3) } });
}

// ------------------------------------------------------------------------------------------------

export function pauseScene(ctx: Ctx): Scene {
  let i = 0;
  /** Outfits this scout can wear: the default look, the Sticker Pack once bought, and regional outfits. */
  const wardrobe = (): (OutfitId | "base")[] => ["base", ...(ctx.state.cosmetics.sticker ? ["sticker" as const] : []), ...ctx.state.cosmetics.outfits];
  const wornOutfitId = (): OutfitId | "base" => ctx.state.cosmetics.wear || (ctx.state.cosmetics.sticker ? "sticker" : "base");
  let age = 0;
  const items = ["CREW", "PACK", "INDEX", "PLAN", "MAP", "GOALS", "ONLINE", "SAVE", "TITLE"];
  const icons = ["♥", "$", "★", "▶", "@", "!", "·", "▼", "◀"];
  const tints: Col[] = [UI.green, UI.gold, UI.pink, UI.teal, [120, 170, 255, 255], [255, 210, 90, 255], [255, 120, 200, 255], [170, 150, 255, 255], UI.textDim];
  const step = 21;
  return {
    id: "pause",
    native: true,
    overlay: true,
    enter() {
      i = 0;
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.dirPressed();
      if (d?.x) {
        // Left/right on the trainer card swaps outfits among the ones owned; no extra menu.
        const worn = wardrobe();
        if (worn.length > 1) {
          const now = worn.indexOf(wornOutfitId());
          ctx.state.cosmetics.wear = worn[(now + d.x + worn.length) % worn.length]!;
          persist(ctx.state);
          ctx.audio.cursor();
        }
      } else if (d) {
        i = wrapIndex(i, items.length, 0, d.y, 1);
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape") || ctx.input.consume("Tab")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (ctx.input.consume("Enter")) {
        ctx.audio.confirm();
        if (i === 0) ctx.push("party");
        else if (i === 1) ctx.push("bag");
        else if (i === 2) ctx.push("index");
        else if (i === 3) ctx.push("plan");
        else if (i === 4) ctx.push("map");
        else if (i === 5) ctx.push("goals");
        else if (i === 6) ctx.push("online");
        else if (i === 7) {
          ctx.save();
          ctx.pop();
          ctx.replace("overworld", { notice: "Saved to this pocket." });
        } else ctx.replace("title");
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = enter(age, reduced);
      g.fill(0, 0, SCREEN_W, SCREEN_H, [8, 10, 24, Math.round(150 * t)]);
      // Menu column.
      const mx = SCREEN_W - 120 + Math.round((1 - t) * 60), my = 12;
      glass(g, mx, my, 110, items.length * step + 10, { r: 8 });
      const pending = goalsPending(ctx.state);
      items.forEach((label, n) => {
        const y = my + 6 + n * step;
        const on = n === i;
        if (on) {
          g.rrect(mx + 5, y, 100, step - 2, 6, withA(tints[n]!, 70));
          g.rrectLine(mx + 5, y, 100, step - 2, 6, tints[n]!);
        }
        g.disc(mx + 17, y + 9, 7, withA(tints[n]!, on ? 255 : 90));
        g.text(mx + 14, y + 6, icons[n]!, on ? UI.paperInk : UI.text);
        g.textShadow(mx + 30, y + 6, `${on ? ">" : " "}${label}`, on ? UI.white : UI.textDim, UI.ink);
        if (label === "GOALS" && pending) {
          const bx = mx + 34 + measureText(`${on ? ">" : " "}${label}`);
          g.disc(bx + 4, y + 9, 5, UI.red);
          g.text(bx + 3, y + 6, "!", UI.white);
        }
        if (on) cursor(g, mx - 8, y + 6, ctx.now, reduced);
      });
      // Trainer card.
      const cx = 12 - Math.round((1 - t) * 60), cy = 12;
      glass(g, cx, cy, 236, 104, { r: 8 });
      g.gradV(cx + 1, cy + 1, 234, 18, withA(UI.teal, 150), withA(UI.bg2, 60), 7);
      g.textShadow(cx + 10, cy + 6, "TRAINER CARD", UI.white, UI.ink);
      const worn = wornOutfit(ctx.state.cosmetics), wearing = wornOutfitId();
      g.rrect(cx + 10, cy + 24, 50, 72, 6, UI.bg0);
      g.gradV(cx + 11, cy + 25, 48, 70, [70, 90, 160, 255], [30, 34, 70, 255], 5);
      g.blit(actorFrame(worn.key, worn.outfit, 1, 0), cx + 19, cy + 30, { scale: 2 });
      if (wardrobe().length > 1) g.textC(cx + 35, cy + 86, "◀ ▶", UI.textDim);
      const kit = [wearing === "base" ? null : ITEMS[wearing].name.toUpperCase().replace(" PACK", ""),
        ctx.state.cosmetics.frame ? "FRAME" : null].filter(Boolean).join(" / ");
      const rx = cx + 70;
      g.textShadow(rx, cy + 26, ctx.state.name, UI.text, UI.ink, 2);
      coin(g, rx, cy + 44, reduced ? 0 : ctx.now / 400);
      g.text(rx + 12, cy + 46, `${ctx.state.money} ${CURRENCY}`, UI.goldHi);
      g.text(rx, cy + 58, `Caught ${ctx.state.caught.length}/${SPECIES_IDS.length}`, UI.text);
      g.text(rx + 84, cy + 58, playTime(ctx.state.playMs), UI.textDim);
      g.text(rx, cy + 70, ctx.state.title ? `TITLE ${ctx.state.title}` : `SEED ${ctx.state.seed}`, ctx.state.title ? UI.goldHi : UI.textMute);
      g.text(rx, cy + 82, kit ? `KIT ${kit}` : "Shirt as-is. Walk.", kit ? UI.teal : UI.textMute);
      // Duel stamps.
      const ids = Object.keys(TRAINERS) as (keyof typeof TRAINERS)[];
      const sx = cx, sy = cy + 110;
      // Every duel on the map gets a stamp: wrap into rows that fit the card.
      const rows = ids.length > 9 ? 2 : 1, perRow = Math.ceil(ids.length / rows);
      const pitch = Math.min(24, Math.floor(216 / perRow));
      glass(g, sx, sy, 236, 18 + rows * 14, { r: 8 });
      g.text(sx + 10, sy + 5, "DUEL STAMPS", UI.textDim);
      g.textR(sx + 226, sy + 5, `${ids.filter((id) => ctx.state.defeatedTrainers.includes(id)).length}/${ids.length}`, UI.textMute);
      ids.forEach((id, n) => {
        const won = ctx.state.defeatedTrainers.includes(id);
        const x = sx + 10 + (n % perRow) * pitch, y = sy + 16 + Math.floor(n / perRow) * 14;
        g.disc(x + 5, y + 5, 5, won ? UI.gold : [40, 44, 72, 255]);
        g.ring(x + 5, y + 5, 5, 5, won ? UI.goldHi : UI.line, 1);
        if (won) g.text(x + 3, y + 2, "★", UI.paperInk);
      });
      hint(g, 14, SCREEN_H - 18, "A", "open", "a");
      hint(g, 70, SCREEN_H - 18, "B", "back", "b");
    },
  };
}

export function partyScene(ctx: Ctx): Scene {
  let i = 0;
  let age = 0;
  return {
    id: "party",
    native: true,
    enter(p?: ScenePayload) {
      i = Math.max(0, Math.min(p?.partyIndex ?? 0, ctx.state.party.length - 1));
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.dirPressed();
      if (d && ctx.state.party.length) {
        if (d.x) {
          const next = i + d.x;
          if (next >= 0 && next < ctx.state.party.length) {
            [ctx.state.party[i], ctx.state.party[next]] = [ctx.state.party[next]!, ctx.state.party[i]!];
            i = next;
            persist(ctx.state);
            ctx.audio.confirm();
          } else ctx.audio.back();
        } else {
          i = wrapIndex(i, ctx.state.party.length, 0, d.y, 1);
          ctx.audio.cursor();
        }
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (ctx.input.consume("Enter") && ctx.state.party[i]) {
        ctx.audio.confirm();
        ctx.push("summary", { partyIndex: i });
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [24, 40, 52, 255], [10, 14, 26, 255]);
      titleBar(g, `CREW ${ctx.state.party.length}/${PARTY_MAX}`, "A file  ◀▶ order  B back", UI.green);
      if (!ctx.state.party.length) {
        glass(g, 60, 70, 264, 70, { r: 8 });
        g.textShadow(76, 82, "CREW EMPTY", UI.text, UI.ink);
        g.para(76, 98, `Six empty slots. ${SPECIES_IDS.length} jokes in the grass. Catch one like you meant the shirt.`, 232, UI.textDim);
        return;
      }
      const t = enter(age, reduced);
      const lead = ctx.state.party.findIndex((m) => m.hp > 0);
      ctx.state.party.forEach((m, n) => {
        const k = reduced ? 1 : easeOut(Math.min(1, (age - n * 40) / 220));
        crewRow(g, m, 8 - Math.round((1 - k) * 40), 28 + n * 30, 236, n === i, ctx.now, reduced, n === lead);
      });
      // Preview.
      const m = ctx.state.party[i]!;
      const sp = SPECIES[m.speciesId];
      const px = 252 + Math.round((1 - t) * 40), py = 28;
      glass(g, px, py, 124, 178, { r: 8 });
      const col = TYPE_COL[sp.types[0]!]!;
      g.gradV(px + 1, py + 1, 122, 76, withA(col.fill, 110), withA(UI.bg1, 40), 7);
      g.ellipse(px + 62, py + 74, 30, 6, [0, 0, 0, 80], 0.5);
      const bob = reduced ? 0 : Math.round(Math.sin(ctx.now / 400) * 2);
      g.blit(creatureSprite(m.speciesId, "front", 2, !!m.shiny), px + 30, py + 10 + bob);
      g.textC(px + 62, py + 82, sp.name, UI.text);
      const stats: [string, number, Col][] = [["ATK", statOf(m, "atk"), UI.pink], ["DEF", statOf(m, "def"), UI.teal], ["SPD", statOf(m, "spd"), UI.gold]];
      const cap = m.level * 2.5 + 8;
      stats.forEach(([label, v, c], n) => statBar(g, px + 8, py + 96 + n * 11, label, v, cap, c, 60));
      g.text(px + 8, py + 132, "MOVES", UI.textMute);
      m.moves.slice(0, 4).forEach((slot, n) => {
        const mv = peekMove(slot.id);
        if (!mv) return;
        g.rrect(px + 8, py + 143 + n * 8, 3, 6, 1, TYPE_COL[mv.type]!.fill);
        g.text(px + 14, py + 142 + n * 8, fitText(mv.name, 100), UI.textDim);
      });
    },
  };
}

export function summaryScene(ctx: Ctx): Scene {
  let i = 0;
  let age = 0;
  let turn = 0;
  return {
    id: "summary",
    native: true,
    enter(p?: ScenePayload) {
      i = p?.partyIndex ?? 0;
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.dirPressed();
      if (d && ctx.state.party.length) {
        i = wrapIndex(i, ctx.state.party.length, d.x, 0, ctx.state.party.length);
        turn = d.x;
        age = ctx.reduced ? age : 0;
        ctx.audio.cursor();
      }
      if (ctx.input.consumeAny("Escape", "Enter")) {
        ctx.audio.back();
        ctx.pop();
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const m = ctx.state.party[i];
      const sp = m ? SPECIES[m.speciesId] : undefined;
      const col = TYPE_COL[sp?.types[0] ?? "cope"]!;
      backdrop(g, ctx.now, reduced, mix(col.rim, UI.bg1, 0.55), [10, 12, 24, 255]);
      if (!m || !sp) {
        glass(g, 60, 70, 264, 60, { r: 8 });
        g.textShadow(76, 82, "NO FILE", UI.text, UI.ink);
        g.para(76, 98, "Nobody to inspect. Fill a slot. Opinions do not count.", 232, UI.textDim);
        return;
      }
      const t = enter(age, reduced);
      // Portrait column.
      const px = 8 - Math.round((1 - t) * turn * 30), py = 8;
      glass(g, px, py, 144, 200, { r: 8 });
      for (let r = 60; r > 0; r -= 4) g.disc(px + 72, py + 70, r, withA(col.fill, 10));
      g.ellipse(px + 72, py + 112, 38, 8, [0, 0, 0, 90], 0.5);
      const bob = reduced ? 0 : Math.round(Math.sin(ctx.now / 420) * 2);
      g.blit(creatureSprite(m.speciesId, "front", 3, !!m.shiny), px + 24, py + 22 + bob);
      g.textShadow(px + 10, py + 124, m.nick, UI.text, UI.ink, 2);
      const lv = `Lv${m.level}`;
      g.textShadow(px + 134 - measureText(lv), py + 128, lv, UI.gold, UI.ink);
      let bx = px + 10;
      for (const type of sp.types) bx += typeBadge(g, bx, py + 142, type) + 3;
      const next = expToNext(m.level);
      g.text(px + 10, py + 160, m.level >= LEVEL_CAP ? "EXP MAX" : `EXP ${m.exp}/${next}`, UI.textDim);
      meter(g, px + 10, py + 171, 124, 4, m.level >= LEVEL_CAP ? 1 : m.exp / next, { tone: { fill: UI.teal, hi: [150, 255, 240, 255], lo: UI.tealDk } });
      g.text(px + 10, py + 181, `CRY ${sp.cry}`, UI.textMute);
      g.textC(px + 72, py + 192, `◀ ${i + 1}/${ctx.state.party.length} ▶`, UI.textDim);
      // Stats + moves.
      const sx = 160, sy = 8;
      glass(g, sx, sy, 216, 84, { r: 8 });
      g.text(sx + 10, sy + 6, "STATS", UI.textMute);
      const hp = maxHp(m);
      const cap = m.level * 2.5 + 8;
      g.text(sx + 10, sy + 18, `HP ${m.hp}/${hp}`, UI.text);
      meter(g, sx + 96, sy + 19, 108, 5, m.hp / hp);
      statBar(g, sx + 10, sy + 30, "ATK", statOf(m, "atk"), cap, UI.pink, 110);
      statBar(g, sx + 10, sy + 41, "DEF", statOf(m, "def"), cap, UI.teal, 110);
      statBar(g, sx + 10, sy + 52, "SPD", statOf(m, "spd"), cap, UI.gold, 110);
      g.text(sx + 10, sy + 66, `DRIP ${m.drip.atk}/${m.drip.def}/${m.drip.spd}`, UI.textMute);
      g.textR(sx + 206, sy + 66, m.hp <= 0 ? "OUT" : m.hp < hp ? "HURT" : "FIT", m.hp <= 0 ? UI.red : m.hp < hp ? UI.yellow : UI.green);
      glass(g, sx, sy + 90, 216, 110, { r: 8 });
      g.text(sx + 10, sy + 96, "MOVES", UI.textMute);
      for (let n = 0; n < 4; n++) {
        const slot = m.moves[n];
        const y = sy + 108 + n * 18;
        const mv = slot ? peekMove(slot.id) : null;
        if (!mv) {
          g.text(sx + 12, y + 2, "--------", UI.textMute);
          g.textR(sx + 204, y + 2, "--/--", UI.textMute);
          continue;
        }
        g.rrect(sx + 8, y - 2, 200, 16, 5, withA(TYPE_COL[mv.type]!.fill, 40));
        typeBadge(g, sx + 11, y, mv.type, true);
        g.text(sx + 50, y + 2, fitText(mv.name, 100), UI.text);
        g.textR(sx + 204, y + 2, `${slot!.pp}/${mv.pp}`, slot!.pp === 0 ? UI.red : UI.textDim);
      }
      g.para(sx + 10, sy + 181, sp.blurb, 196, UI.textMute, 9, 2);

    },
  };
}

export function bagScene(ctx: Ctx): Scene {
  let i = 0;
  let note: string | null = null;
  let age = 0;
  const list = () =>
    (Object.keys(ITEMS) as ItemId[]).map((id) => ({ id, n: bagCount(ctx.state, id) })).filter((x) => x.n > 0);

  return {
    id: "bag",
    native: true,
    enter() {
      i = 0;
      note = null;
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      if (note) {
        if (ctx.input.consumeAny("Enter", "Escape")) note = null;
        return;
      }
      const items = list();
      i = Math.min(i, Math.max(0, items.length - 1));
      const d = ctx.input.dirPressed();
      if (d && items.length) {
        i = wrapIndex(i, items.length, 0, d.y, 1);
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (ctx.input.consume("Enter") && items[i]) {
        const it = ITEMS[items[i]!.id];
        if (it.kind === "heal") {
          const m = ctx.state.party.find((p) => p.hp < maxHp(p));
          if (!m) {
            note = "Nobody asked for a sip. The fridge can wait.";
            return;
          }
          bagAdd(ctx.state, it.id, -1);
          i = Math.min(i, Math.max(0, list().length - 1));
          healMon(m, itemHeal(it, maxHp(m)));
          ctx.audio.heal();
          persist(ctx.state);
          note = `${m.nick} drinks ${it.name}.`;
        } else {
          note = it.desc;
        }
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [48, 36, 28, 255], [14, 12, 20, 255]);
      titleBar(g, "PACK", `${ctx.state.money} ${CURRENCY}`, UI.gold);
      const items = list();
      if (!items.length) {
        glass(g, 60, 70, 264, 70, { r: 8 });
        g.textShadow(76, 82, "PACK EMPTY", UI.text, UI.ink);
        g.para(76, 98, "No tins, no sips. The dépanneur is two doors east. Late stock for a long roster.", 232, UI.textDim);
        return;
      }
      const t = enter(age, reduced);
      const lx = 8, ly = 28, lw = 200;
      glass(g, lx, ly, lw, 180, { r: 8 });
      const first = Math.max(0, Math.min(i - 6, items.length - 7));
      items.slice(first, first + 7).forEach((it, row) => {
        const n = first + row;
        const y = ly + 6 + row * 24;
        const on = n === i;
        if (on) {
          g.rrect(lx + 5, y, lw - 10, 22, 6, withA(UI.gold, 50));
          g.rrectLine(lx + 5, y, lw - 10, 22, 6, UI.gold);
        }
        g.blit(itemIcon(it.id), lx + 10, y);
        g.textShadow(lx + 34, y + 7, `${on ? ">" : " "}${ITEMS[it.id].name}`, on ? UI.goldHi : UI.text, UI.ink);
        g.textR(lx + lw - 12, y + 7, `x${it.n}`, UI.textDim);
      });
      const cur = items[i];
      if (cur) {
        const def = ITEMS[cur.id];
        const dx = 216 + Math.round((1 - t) * 30), dy = 28;
        glass(g, dx, dy, 160, 180, { r: 8 });
        g.disc(dx + 80, dy + 40, 28, withA(UI.gold, 30));
        g.blit(itemIcon(cur.id), dx + 62, dy + 18, { scale: 2 });
        g.textC(dx + 80, dy + 70, def.name, UI.goldHi);
        g.textC(dx + 80, dy + 82, `x${cur.n} in pack`, UI.textDim);
        g.para(dx + 10, dy + 98, def.desc, 140, UI.text, 11, 6);
        hint(g, dx + 10, dy + 164, "A", def.kind === "heal" ? "use" : "read", "a");
      }
      if (note) dialog(g, note, { more: true, now: ctx.now, reduced });
    },
  };
}

export function shopScene(ctx: Ctx): Scene {
  let i = 0;
  let note: string | null = null;
  let age = 0;
  let bought = -1000;
  const unlocked = (id: ItemId) => !ITEMS[id].requiresTrainer || ctx.state.defeatedTrainers.includes(ITEMS[id].requiresTrainer!);
  /** Cosmetics are owned once: the Sticker Pack and Frame Tint as flags, regional outfits in a list. */
  const owns = (id: ItemId) => id === "sticker" || id === "frame" ? ctx.state.cosmetics[id]
    : ITEMS[id].kind === "cosmetic" && ctx.state.cosmetics.outfits.includes(id as OutfitId);
  // Every stall sells the shared stock; regional stalls add their own outfit at the end.
  let stock: ItemId[] = SHOP_STOCK;
  return {
    id: "shop",
    native: true,
    enter() {
      i = 0;
      stock = stallStock(zoneAt(ctx.state.x, ctx.state.y));
      note = null;
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.dirPressed();
      if (d) {
        i = wrapIndex(i, stock.length, 0, d.y, 1);
        note = null;
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        persist(ctx.state);
        ctx.pop();
        return;
      }
      if (ctx.input.consume("Enter")) {
        const id = stock[i]!;
        const it = ITEMS[id];
        if (!unlocked(id)) {
          note = `${TRAINERS[it.requiresTrainer!].name} has the recipe. Duel in Cope Cliffs.`;
          ctx.audio.back();
          return;
        }
        if (owns(id)) {
          note = "Already owned. The stall does not do doubles.";
          ctx.audio.back();
          return;
        }
        if (ctx.state.money < it.price) {
          ctx.audio.fail();
          note = "Not enough coins. Wallet's a dry reed.";
          return;
        }
        ctx.state.money -= it.price;
        if (it.kind === "cosmetic") {
          if (id === "sticker") { ctx.state.cosmetics.sticker = true; ctx.state.cosmetics.wear = "sticker"; }
          else if (id === "frame") ctx.state.cosmetics.frame = true;
          else { ctx.state.cosmetics.outfits.push(id as OutfitId); ctx.state.cosmetics.wear = id as OutfitId; }
        } else bagAdd(ctx.state, id, 1);
        ctx.audio.buy();
        bought = age;
        persist(ctx.state);
        note =
          it.kind === "cosmetic"
            ? `Bought ${it.name}. On you now${it.stall ? "; Pause ◀▶ swaps outfits" : ""}. ${ctx.state.money} coins left.`
            : `Bought ${it.name}. Pack x${bagCount(ctx.state, id)}. ${ctx.state.money} coins left.`;
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      g.copyRegion(interior("shop", isNight(ctx)), 0, 0, SCREEN_W, SCREEN_H, 0, 0);
      const t = enter(age, reduced);
      // Money pill.
      const money = `${ctx.state.money}`;
      const mw = measureText(money) + 34;
      glass(g, SCREEN_W - mw - 8, 8, mw, 18, { r: 8 });
      coin(g, SCREEN_W - mw - 1, 12, reduced ? 0 : ctx.now / 300);
      const pop = !reduced && age - bought < 300 ? 1 : 0;
      g.textShadow(SCREEN_W - mw + 12, 13 - pop, money, UI.goldHi, UI.ink);
      if (!stock.length) {
        dialog(g, "Nothing on the board. The latch went home. Come back after restock.", { name: "STALL WIPED", now: ctx.now, reduced });
        return;
      }
      // Stock list.
      const lx = 8 - Math.round((1 - t) * 60), ly = 8, lw = 196;
      glass(g, lx, ly, lw, 146, { r: 8 });
      g.textShadow(lx + 10, ly + 6, `STALL  ${ctx.state.money} ${CURRENCY}`, UI.text, UI.ink);
      g.textR(lx + lw - 10, ly + 6, `${i + 1}/${stock.length}`, UI.textDim);
      const first = Math.max(0, Math.min(i - 4, stock.length - 5));
      stock.slice(first, first + 5).forEach((id, row) => {
        const n = first + row;
        const it = ITEMS[id];
        const y = ly + 20 + row * 25;
        const on = n === i;
        const lock = !unlocked(id);
        if (on) {
          g.rrect(lx + 5, y, lw - 10, 23, 6, withA(UI.gold, 50));
          g.rrectLine(lx + 5, y, lw - 10, 23, 6, UI.gold);
        }
        g.blit(itemIcon(id), lx + 10, y + 1, { tint: lock ? [40, 40, 60, 255] : undefined, tintAmt: lock ? 0.7 : 0 });
        g.textShadow(lx + 34, y + 4, `${on ? ">" : " "}${it.name}${lock ? " [locked]" : ""}`, lock ? UI.textMute : on ? UI.goldHi : UI.text, UI.ink);
        const owned = it.kind === "cosmetic" ? (owns(id) ? "owned" : "") : bagCount(ctx.state, id) ? `have ${bagCount(ctx.state, id)}` : "";
        if (owned) g.text(lx + 34, y + 14, owned, UI.textMute);
        const price = `${it.price}`;
        coin(g, lx + lw - 20 - measureText(price), y + 6);
        g.textR(lx + lw - 10, y + 8, price, UI.goldHi);
      });
      const id = stock[i]!;
      const it = ITEMS[id];
      const have = it.kind === "cosmetic"
        ? (owns(id) ? "  yours" : "")
        : bagCount(ctx.state, id) ? `  pack x${bagCount(ctx.state, id)}` : "";
      dialog(g, note ?? (unlocked(id) ? `${it.desc}` : "Win Ilex's duel in Cope Cliffs."), { name: note ? "STALL" : it.name, more: !!note, now: ctx.now, reduced, age });
      if (!note) {
        const label = unlocked(id) ? `buy ${it.name}  ${it.price} coins${have}` : "locked";
        const w = measureText(label) + 20;
        hint(g, SCREEN_W - w - 22, SCREEN_H - 22, "A", label, "a");
      }
      if (ctx.state.cosmetics.frame) frameTint(g);
      doorFade(g, age, reduced);
    },
  };
}

export function clinicScene(ctx: Ctx): Scene {
  let confirm = "A  done    B  done";
  let age = 0;
  let before: number[] = [];
  return {
    id: "clinic",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      before = ctx.state.party.map((m) => m.hp / maxHp(m));
      const needed = ctx.state.party.some(
        (m) => m.hp < maxHp(m) || m.moves.some((s) => s.pp < moveById(s.id).pp),
      );
      clinicHeal(ctx.state);
      persist(ctx.state);
      ctx.audio.heal();
      if (!ctx.state.party.length) confirm = "No crew to mend. B  leave.";
      else if (needed) confirm = "Kettle punched the card. HP and PP full. A: done.";
      else confirm = "Already full. The cot is decorative. A: done.";
    },
    exit() {},
    update() {
      age += ctx.dt;
      if (ctx.input.consumeAny("Enter", "Escape")) {
        ctx.audio.confirm();
        ctx.pop();
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      g.copyRegion(interior("clinic", isNight(ctx)), 0, 0, SCREEN_W, SCREEN_H, 0, 0);
      glass(g, 8, 8, 150, 18, { r: 8 });
      g.textShadow(16, 13, "COPE CLINIC  kettle on", UI.text, UI.ink);
      if (!ctx.state.party.length) {
        dialog(g, `Nobody on the cot. ${SPECIES_IDS.length} jokes out there. The kettle can wait.`, { name: "NO CREW", now: ctx.now, reduced });
        return;
      }
      const heal = reduced ? 1 : easeOut(Math.min(1, Math.max(0, age - 200) / 900));
      ctx.state.party.forEach((m, n) => {
        const x = 8 + (n % 2) * 122, y = 32 + Math.floor(n / 2) * 32;
        const k = reduced ? 1 : easeOut(Math.min(1, (age - n * 60) / 240));
        const yy = y + Math.round((1 - k) * 20);
        glass(g, x, yy, 118, 28, { r: 7, alpha: 230 });
        g.blit(creatureIcon(m.speciesId, !!m.shiny), x + 3, yy - 3);
        g.textShadow(x + 36, yy + 4, fitText(m.nick, 76), UI.text, UI.ink);
        const hp = (before[n] ?? 1) + (1 - (before[n] ?? 1)) * heal;
        const hpText = `${Math.round(hp * maxHp(m))}/${maxHp(m)}`;
        meter(g, x + 36, yy + 17, 76 - measureText(hpText) - 4, 5, hp);
        g.textR(x + 112, yy + 15, hpText, UI.textDim);
        if (!reduced && heal < 1 && (before[n] ?? 1) < 1) {
          for (let s = 0; s < 3; s++) {
            const py = yy + 20 - ((age / 6 + s * 9 + n * 5) % 24);
            g.text(x + 8 + s * 9, py, "+", withA(UI.green, 200));
          }
        }
      });
      dialog(g, confirm, { name: "NURSE KETTLE", more: true, now: ctx.now, reduced, age });
      if (ctx.state.cosmetics.frame) frameTint(g);
      doorFade(g, age, reduced);
    },
  };
}

export function pcScene(ctx: Ctx): Scene {
  let i = 0;
  let swapI: number | null = null;
  let note = "B  leave.";
  let age = 0;
  return {
    id: "pc",
    native: true,
    enter() {
      i = 0;
      age = enterAge(ctx.shot);
      note = ctx.state.pc.length
        ? "A  pull into crew.  B  leave."
        : "Crate is empty.  B  leave.";
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.dirPressed();
      if (swapI !== null) {
        if (d && ctx.state.party.length) {
          swapI = wrapIndex(swapI, ctx.state.party.length, 0, d.y, 1);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          swapI = null;
          note = "B  leave.";
          ctx.audio.back();
          return;
        }
        if (ctx.input.consume("Enter") && ctx.state.party[swapI]) {
          const mon = ctx.state.pc[i]!;
          ctx.state.pc[i] = ctx.state.party[swapI]!;
          ctx.state.party[swapI] = mon;
          swapI = null;
          note = `${mon.nick} joins the crew.  B  leave.`;
          ctx.audio.confirm();
          persist(ctx.state);
        }
        return;
      }
      const n = ctx.state.pc.length;
      if (d && n) {
        i = wrapIndex(i, n, 0, d.y, 1);
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        persist(ctx.state);
        ctx.pop();
        return;
      }
      if (ctx.input.consume("Enter") && ctx.state.pc[i]) {
        if (ctx.state.party.length >= PARTY_MAX) {
          swapI = 0;
          note = "Crew full. Pick who sits out.";
          return;
        }
        const mon = ctx.state.pc[i]!;
        ctx.state.pc.splice(i, 1);
        const where = stashMon(ctx.state, mon);
        if (where === "pc") {
          note = `Crew full (${PARTY_MAX}). It bounced back.  B  leave.`;
        } else {
          restoreAll(mon);
          persist(ctx.state);
          note = `${mon.nick} joined the crew.  B  leave.`;
        }
        i = 0;
        if (!ctx.state.pc.length && where === "party") {
          note = `${mon.nick} joined the crew. Crate empty.  B  leave.`;
        }
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [20, 46, 70, 255], [8, 12, 26, 255]);
      if (swapI !== null) {
        titleBar(g, "SWAP WITH WHICH MEME?", "A swap  B keep", UI.pink);
        ctx.state.party.forEach((m, n) => crewRow(g, m, 8, 28 + n * 30, 236, n === swapI, ctx.now, reduced, false));
        const incoming = ctx.state.pc[i];
        if (incoming) {
          glass(g, 252, 28, 124, 120, { r: 8 });
          g.text(260, 34, "INCOMING", UI.textMute);
          g.blit(creatureSprite(incoming.speciesId, "front", 2, !!incoming.shiny), 282, 46);
          g.textC(314, 114, `${incoming.nick} Lv${incoming.level}`, UI.text);
        }
        dialog(g, "A: swap. B: keep the crew.", { now: ctx.now, reduced, x: 252, w: 124, y: 156, h: 50 });
        return;
      }
      titleBar(g, `MEME BOX  ${ctx.state.pc.length} stored`, undefined, [120, 170, 255, 255]);
      if (!ctx.state.pc.length) {
        glass(g, 60, 60, 264, 70, { r: 8 });
        g.textShadow(76, 72, "CRATE EMPTY", UI.text, UI.ink);
        g.para(76, 88, "Overflow sleeps here. Catch a seventh. The caisse files the rest.", 232, UI.textDim);
      } else {
        const visible = 5;
        const first = Math.max(0, Math.min(i - visible + 1, ctx.state.pc.length - visible));
        ctx.state.pc.slice(first, first + visible).forEach((m, n) => {
          const on = n + first === i;
          crewRow(g, m, 8, 28 + n * 30, 236, on, ctx.now, reduced, false);
        });
        const cur = ctx.state.pc[i]!;
        glass(g, 252, 28, 124, 120, { r: 8 });
        g.blit(creatureSprite(cur.speciesId, "front", 2, !!cur.shiny), 282, 36 + (reduced ? 0 : Math.round(Math.sin(ctx.now / 400) * 2)));
        g.textC(314, 104, `${cur.nick} Lv${cur.level}`, UI.text);
        let bx = 262;
        for (const type of SPECIES[cur.speciesId].types) bx += typeBadge(g, bx, 118, type, true) + 3;
      }
      dialog(g, note, { now: ctx.now, reduced, y: 180, h: 30 });
    },
  };
}

const INDEX_COLS = 6;
const INDEX_ROWS = 3;

export function indexScene(ctx: Ctx): Scene {
  let i = 0;
  let detail = false;
  let page = 0;
  let unrecorded = false;
  let age = 0;
  return {
    id: "index",
    native: true,
    enter() {
      i = 0;
      detail = false;
      page = 0;
      unrecorded = false;
      age = enterAge(ctx.shot);
    },
    exit() {},
    update() {
      age += ctx.dt;
      if (unrecorded) {
        if (ctx.input.consumeAny("Enter", "Escape")) unrecorded = false;
        return;
      }
      if (detail) {
        if (ctx.input.consume("Escape")) {
          detail = false;
          ctx.audio.back();
        } else if (ctx.input.consumeAny("Enter", "ArrowRight")) {
          page = (page + 1) % 3;
          ctx.audio.cursor();
        } else if (ctx.input.consume("ArrowLeft")) {
          page = (page + 2) % 3;
          ctx.audio.cursor();
        }
        return;
      }
      const d = ctx.input.dirPressed();
      if (d) {
        i = wrapIndex(i, SPECIES_IDS.length, d.x, d.y, INDEX_COLS);
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
      } else if (ctx.input.consume("Enter")) {
        if (ctx.state.seen.includes(SPECIES_IDS[i]!) || ctx.state.caught.includes(SPECIES_IDS[i]!)) {
          detail = true;
          page = 0;
          ctx.audio.confirm();
        } else {
          unrecorded = true;
          ctx.audio.back();
        }
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      if (detail) {
        drawIndexDetail(ctx, SPECIES_IDS[i]!, page);
        return;
      }
      backdrop(g, ctx.now, reduced, [58, 24, 44, 255], [14, 10, 20, 255]);
      titleBar(g, `INDEX  ${ctx.state.caught.length}/${SPECIES_IDS.length} owned`, `seen ${ctx.state.seen.length}`, UI.pink);
      const per = INDEX_COLS * INDEX_ROWS;
      const first = Math.floor(i / per) * per;
      SPECIES_IDS.slice(first, first + per).forEach((id, n) => {
        const col = n % INDEX_COLS, row = Math.floor(n / INDEX_COLS);
        const x = 8 + col * 62, y = 28 + row * 52;
        const got = ctx.state.caught.includes(id);
        const seen = got || ctx.state.seen.includes(id);
        const on = first + n === i;
        glass(g, x, y, 58, 48, { r: 6, tone: on ? UI.bg3 : UI.bg1, border: on ? UI.gold : got ? withA(UI.gold, 120) : UI.line, shadow: false });
        const bob = on && !reduced ? Math.round(Math.abs(Math.sin(ctx.now / 200)) * 2) : 0;
        if (seen) g.blit(creatureSprite(id, "front", 1), x + 13, y + 4 - bob);
        else g.blit(creatureSprite(id, "front", 1), x + 13, y + 4, { tint: [20, 18, 36, 255], tintAmt: 1 });
        g.text(x + 3, y + 3, String(first + n + 1).padStart(3, "0"), UI.textMute);
        if (got) g.text(x + 49, y + 3, "★", UI.gold);
        g.textC(x + 29, y + 38, fitText(seen ? SPECIES[id].name : "?????", 54), on ? UI.goldHi : seen ? UI.text : UI.textMute);
        if (on) focus(g, x - 1, y - 1, 60, 50, ctx.now, reduced, 7);
      });
      // Selected entry bar.
      const cur = SPECIES_IDS[i]!;
      const curGot = ctx.state.caught.includes(cur);
      const curSeen = curGot || ctx.state.seen.includes(cur);
      glass(g, 8, 186, 368, 24, { r: 6, shadow: false });
      g.textShadow(16, 194, `>${curSeen ? SPECIES[cur].name : "????????"}`, curSeen ? UI.goldHi : UI.textMute, UI.ink);
      let tx = 112;
      if (curSeen) for (const type of SPECIES[cur].types) tx += typeBadge(g, tx, 192, type, true) + 3;
      g.text(Math.max(tx + 4, 176), 194, curGot ? "OWNED" : curSeen ? "SEEN" : "UNKNOWN", curGot ? UI.gold : UI.textDim);
      g.textR(250, 194, `${first + 1}-${Math.min(first + per, SPECIES_IDS.length)}/${SPECIES_IDS.length}`, UI.textMute);
      hint(g, 262, 192, "A", "file", "a");
      hint(g, 314, 192, "B", "back", "b");
      if (unrecorded) dialog(g, "Meet it in the grass or a duel. Then it gets a file. A/B: list.", { name: "NO FIELD RECORD", more: true, now: ctx.now, reduced });
    },
  };
}

function drawIndexDetail(ctx: Ctx, id: SpeciesId, page: number): void {
  const g = ctx.g;
  const reduced = ctx.reduced;
  const sp = SPECIES[id];
  const col = TYPE_COL[sp.types[0]!]!;
  backdrop(g, ctx.now, reduced, mix(col.rim, UI.bg1, 0.5), [10, 10, 22, 255]);
  titleBar(g, sp.name, `${["PROFILE", "HABITAT", "KIT"][page]!}  ${page + 1}/3`, col.fill);
  glass(g, 8, 28, 120, 164, { r: 8 });
  for (let r = 48; r > 0; r -= 4) g.disc(68, 86, r, withA(col.fill, 12));
  g.ellipse(68, 128, 34, 7, [0, 0, 0, 90], 0.5);
  g.blit(creatureSprite(id, "front", 3), 20, 36 + (reduced ? 0 : Math.round(Math.sin(ctx.now / 420) * 2)));
  let bx = 16;
  for (const type of sp.types) bx += typeBadge(g, bx, 140, type) + 3;
  g.text(16, 156, ctx.state.caught.includes(id) ? "OWNED" : "SEEN", ctx.state.caught.includes(id) ? UI.gold : UI.textDim);
  g.text(16, 168, `CRY ${sp.cry}`, UI.textMute);
  const x = 136, y = 28, w = 240;
  glass(g, x, y, w, 164, { r: 8 });
  if (page === 0) {
    g.para(x + 10, y + 10, sp.blurb, w - 20, UI.text, 11, 6);
    g.hline(x + 10, y + 88, w - 20, withA(UI.line, 200));
    g.para(x + 10, y + 96, guideEvolution(id), w - 20, UI.teal, 11, 5);
  } else if (page === 1) {
    const habitats = guideHabitats(id, ctx.state.defeatedTrainers.includes("vell"));
    g.text(x + 10, y + 10, "WILD GRASS", UI.textMute);
    if (!habitats.length) {
      g.para(x + 10, y + 26, "No grass filed this joke. Yet. Walk anyway.", w - 20, UI.text);
      g.para(x + 10, y + 60, guideEvolution(id), w - 20, UI.teal);
    } else {
      habitats.forEach((row, n) => {
        const region = row.region === "north" ? " N" : row.region === "south" ? " S" : "";
        const ry = y + 26 + n * 20;
        g.rrect(x + 8, ry - 3, w - 16, 16, 5, withA(UI.bg3, 160));
        g.text(x + 14, ry + 1, `${ZONES[row.zone].name}${region}`, UI.text);
        g.textR(x + w - 14, ry + 1, `Lv${row.lo}-${row.hi}`, UI.gold);
      });
      if (habitats.some((row) => row.region !== "all")) g.text(x + 10, y + 148, "N north beds   S south beds", UI.textMute);
    }
  } else {
    g.text(x + 10, y + 8, "STANDARD KIT", UI.textMute);
    sp.moves.forEach((mid, n) => {
      const move = moveById(mid);
      const ry = y + 22 + n * 34;
      g.rrect(x + 8, ry - 2, w - 16, 30, 6, withA(TYPE_COL[move.type]!.fill, 40));
      g.text(x + 14, ry + 2, move.name, UI.text);
      typeBadge(g, x + w - 56, ry, move.type, true);
      g.text(x + 14, ry + 14, fitText(`${move.power ? `POW ${move.power}` : "STATUS"}  ${moveEffectLabel(move)}`, w - 80), UI.textDim);
      g.textR(x + w - 14, ry + 14, `PP ${move.pp}`, UI.textDim);
    });
  }
  hint(g, 10, SCREEN_H - 18, "◀▶", "pages", "n");
  hint(g, 86, SCREEN_H - 18, "A", "next", "a");
  hint(g, 136, SCREEN_H - 18, "B", "list", "b");
}

/** Short fade from black when stepping through a door. */
function doorFade(g: Gfx, age: number, reduced: boolean): void {
  if (reduced || age >= 220) return;
  g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, Math.round(255 * (1 - age / 220))]);
}

function frameTint(g: Gfx): void {
  for (let i = 0; i < 3; i++) g.rrectLine(i, i, SCREEN_W - i * 2, SCREEN_H - i * 2, 8, [255, 200, 90, 150 - i * 45]);
}

